<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2" color="primary">mdi-file-document-outline</v-icon>
      Allgemeine Nutzungsbedingungen
    </v-card-title>

    <v-card-text class="text-body-2" style="max-height: 60vh; overflow-y: auto;">
      <p class="text-body-1 font-weight-medium mb-4">
        Bitte lies die folgenden Nutzungsbedingungen sorgfältig durch, bevor du diese Anwendung verwendest.
      </p>

      <!-- 1. Nicht-kommerzieller Zweck -->
      <div class="mb-4">
        <div class="text-subtitle-2 font-weight-bold mb-1">
          <v-icon size="18" class="mr-1">mdi-currency-usd-off</v-icon>
          1. Nicht-kommerzieller Zweck
        </div>
        <p>
          Diese Webanwendung wird ausschließlich zu nicht-kommerziellen Zwecken betrieben.
          Es werden keine Einnahmen durch die Nutzung dieser Seite erzielt. Es handelt sich um ein
          internes Werkzeug ohne Gewinnerzielungsabsicht. Es besteht kein Anspruch auf
          Verfügbarkeit, Funktionalität oder Weiterentwicklung der Anwendung.
        </p>
      </div>

      <!-- 2. Keine sensiblen Daten -->
      <div class="mb-4">
        <div class="text-subtitle-2 font-weight-bold mb-1">
          <v-icon size="18" class="mr-1">mdi-shield-lock-outline</v-icon>
          2. Datenschutz und sensible Daten
        </div>
        <p>
          Es ist ausdrücklich <strong>untersagt</strong>, private, personenbezogene oder
          firmenbezogene vertrauliche Daten in dieser Anwendung einzugeben oder zu speichern.
          Dazu gehören unter anderem:
        </p>
        <ul class="ml-4 mt-1">
          <li>Persönliche Kontaktdaten (private Adressen, Telefonnummern, etc.)</li>
          <li>Passwörter oder Zugangsdaten für andere Systeme</li>
          <li>Vertrauliche Geschäfts- oder Betriebsgeheimnisse</li>
          <li>Personenbezogene Daten Dritter ohne deren Einwilligung</li>
        </ul>
        <p class="mt-2">
          Die Nutzung erfolgt auf eigene Verantwortung. Der Betreiber übernimmt keine
          Verantwortung für Daten, die entgegen dieser Regelung eingegeben werden.
        </p>
      </div>

      <!-- 3. Haftungsausschluss -->
      <div class="mb-4">
        <div class="text-subtitle-2 font-weight-bold mb-1">
          <v-icon size="18" class="mr-1">mdi-alert-circle-outline</v-icon>
          3. Haftungsausschluss
        </div>
        <p>
          Die Nutzung dieser Anwendung erfolgt auf eigenes Risiko. Der Betreiber übernimmt
          <strong>keinerlei Haftung</strong> für:
        </p>
        <ul class="ml-4 mt-1">
          <li>Datenverlust oder -beschädigung jeglicher Art</li>
          <li>Fehlerhafte Darstellung, Berechnung oder Verarbeitung von Daten</li>
          <li>Ausfälle, Unterbrechungen oder Nichterreichbarkeit der Anwendung</li>
          <li>Schäden, die durch die Nutzung oder Nichtnutzung der bereitgestellten Informationen entstehen</li>
          <li>Schäden durch unbefugten Zugriff Dritter auf gespeicherte Daten</li>
        </ul>
        <p class="mt-2">
          Die Anwendung wird ohne jegliche Gewährleistung bereitgestellt (<em>"as is"</em>).
          Es wird keine Garantie für die Richtigkeit, Vollständigkeit oder Aktualität der
          angezeigten Daten übernommen.
        </p>
      </div>

      <!-- 4. Sonstige Bestimmungen -->
      <div class="mb-4">
        <div class="text-subtitle-2 font-weight-bold mb-1">
          <v-icon size="18" class="mr-1">mdi-gavel</v-icon>
          4. Sonstige Bestimmungen
        </div>
        <ul class="ml-4">
          <li>
            Der Betreiber behält sich das Recht vor, diese Nutzungsbedingungen jederzeit und
            ohne Vorankündigung zu ändern. Die fortgesetzte Nutzung nach Änderungen gilt als
            Zustimmung zu den aktualisierten Bedingungen.
          </li>
          <li>
            Der Betreiber behält sich vor, Benutzerkonten ohne Angabe von Gründen zu
            sperren oder zu löschen, insbesondere bei Verstoß gegen diese Nutzungsbedingungen.
          </li>
          <li>
            Sollte eine Bestimmung dieser Nutzungsbedingungen unwirksam sein oder werden,
            bleibt die Wirksamkeit der übrigen Bestimmungen davon unberührt.
          </li>
          <li>
            Es gilt das Recht der Bundesrepublik Deutschland.
          </li>
        </ul>
      </div>

      <v-divider class="my-3" />

      <p class="text-caption text-medium-emphasis">
        Stand: März 2026 | Bei Fragen wende dich an den Betreiber der Anwendung.
      </p>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        :variant="globalSettings.globalButtonVariant"
        size="large"
        @click="accept"
      >
        Akzeptieren und fortfahren
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { useGlobalSetting } from '@/lib/utils'

interface Props {
  onAccept?: () => void
  onClose?: () => void
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'accept'): void
  (e: 'close'): void
}>()

const globalSettings = useGlobalSetting()

const STORAGE_KEY = 'agb_accepted'

const accept = () => {
  localStorage.setItem(STORAGE_KEY, new Date().toISOString())
  emit('accept')
  props.onAccept?.()
  props.onClose?.()
}
</script>
